import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  watchesApi,
  Watch,
  WatchPatch,
  SPEC_TEXT_FIELDS,
  SPEC_LABELS,
} from '../watchesApi';
import { seedPatchFor } from '../watchSpecs';
import { api, GeneralNewsItem } from '../api';
import { buildTerms } from '../watchMatch';
import { errorMessage as errText } from '../errors';
import { watchImages } from '../watchImages';
import { newsThumbClass, useSettings } from '../settings/SettingsContext';
import { hostOf, scrapeInto } from '../scrapeApi';
import BRANDS from '../data/brands.json';

const BRAND_LIST = BRANDS as string[];

/** How many mentions to list before falling back to a count. */
const NEWS_SHOWN = 12;



export default function WatchDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // The same display setting the news feed uses. It covers news thumbnails
  // wherever they appear; the owner's own photo of the watch is theirs and is
  // left alone.
  const { thumbs } = useSettings();

  const [watch, setWatch] = useState<Watch | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState('');

  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  const [news, setNews] = useState<GeneralNewsItem[] | null>(null);

  // The bucket is private, so a stored path has to be signed before it can be
  // rendered. null means "nothing to show" -- either no photo or a signing
  // failure, both of which simply hide the element.
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Identity fields (brand / model / reference) are edited here rather than on
  // the list: the list is now a browse surface with no per-row actions.
  const [editingCore, setEditingCore] = useState(false);
  const [coreDraft, setCoreDraft] = useState({
    brand: '',
    model: '',
    reference: '',
    source_url: '',
  });
  const [savingCore, setSavingCore] = useState(false);

  // Scrape status. `scrapeNote` is the one-line outcome under the Details
  // card; warnings are the things that half-worked (usually the image).
  const [scraping, setScraping] = useState(false);
  const [scrapeNote, setScrapeNote] = useState('');
  const [scrapeWarnings, setScrapeWarnings] = useState<string[]>([]);

  const [editingSpecs, setEditingSpecs] = useState(false);
  const [specDraft, setSpecDraft] = useState<Record<string, string>>({});
  const [savingSpecs, setSavingSpecs] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    watchesApi
      .get(id)
      .then(w => {
        if (cancelled) return;
        if (!w) {
          setMissing(true);
          return;
        }
        setWatch(w);
        setNotes(w.notes ?? '');
      })
      .catch(err => {
        if (!cancelled) setError(errText(err, 'Could not load that watch.'));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Headlines mentioning this watch, using the same matcher the news list
  // uses, narrowed to a single watch. The news file is already in memory, so
  // this is a filter rather than a request.
  useEffect(() => {
    if (!watch) return;
    let cancelled = false;
    api
      .getGeneralNews({ matchTerms: buildTerms([watch]) })
      .then(items => {
        if (!cancelled) setNews(items);
      })
      .catch(() => {
        if (!cancelled) setNews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [watch]);

  // Sign whatever path the row carries. Re-runs when the path changes, so an
  // upload or removal swaps the rendered image without a reload.
  useEffect(() => {
    const path = watch?.image_url;
    if (!path) {
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    watchImages.signedUrl(path).then(url => {
      if (!cancelled) setImageUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [watch?.image_url]);

  async function handlePickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Clear the input straight away, so picking the same file twice still fires
    // a change event.
    e.target.value = '';
    if (!file || !watch) return;

    setUploading(true);
    try {
      const path = await watchImages.upload(watch.id, file);
      // The path is derived from the watch id, so replacing a photo writes the
      // same value. Bust the signed URL rather than trusting the row to change.
      setWatch(await watchesApi.update(watch.id, { image_url: path }));
      const url = await watchImages.signedUrl(path);
      setImageUrl(url ? `${url}#${Date.now()}` : null);
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not upload that image.'));
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveImage() {
    if (!watch?.image_url) return;
    if (!window.confirm('Remove this photo?')) return;
    setUploading(true);
    try {
      await watchImages.remove(watch.image_url);
      setWatch(await watchesApi.update(watch.id, { image_url: null }));
      setImageUrl(null);
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not remove that photo.'));
    } finally {
      setUploading(false);
    }
  }

  function startEditCore() {
    if (!watch) return;
    setCoreDraft({
      brand: watch.brand,
      model: watch.model,
      reference: watch.reference ?? '',
      source_url: watch.source_url ?? '',
    });
    setEditingCore(true);
  }

  async function handleSaveCore() {
    if (!watch) return;
    if (!coreDraft.brand.trim() || !coreDraft.model.trim()) return;
    setSavingCore(true);
    try {
      const before = watch.source_url ?? '';
      const saved = await watchesApi.update(watch.id, coreDraft);
      setWatch(saved);
      setEditingCore(false);
      setError('');

      // Saving a URL is the thing that starts a scrape -- that is the whole
      // interaction. Only on a change, so re-saving the notes-adjacent fields
      // does not re-fetch a page nothing has asked about again.
      const after = saved.source_url ?? '';
      if (after && after !== before) await runScrape(saved);
    } catch (err) {
      setError(errText(err, 'Could not save those changes.'));
    } finally {
      setSavingCore(false);
    }
  }

  /**
   * Read the watch's source page and fill in whatever is still blank.
   *
   * Never overwrites: applyScrape decides, and it only writes empty columns.
   * That is what makes the "Re-fetch" action safe to press at any time.
   */
  async function runScrape(target: Watch) {
    if (!target.source_url) return;
    setScraping(true);
    setScrapeNote('');
    setScrapeWarnings([]);
    try {
      const outcome = await scrapeInto(target);
      setWatch(outcome.watch);
      setScrapeNote(outcome.note);
      setScrapeWarnings(outcome.warnings);
    } catch (err) {
      setScrapeNote('');
      setError(errText(err, 'Could not read that page.'));
    } finally {
      setScraping(false);
    }
  }

  function startEditSpecs() {
    if (!watch) return;
    const draft: Record<string, string> = {};
    for (const key of SPEC_TEXT_FIELDS) draft[key] = watch[key] ?? '';
    draft.year = watch.year == null ? '' : String(watch.year);
    setSpecDraft(draft);
    setEditingSpecs(true);
  }

  async function handleSaveSpecs() {
    if (!watch) return;
    setSavingSpecs(true);
    try {
      const patch: WatchPatch = {};
      for (const key of SPEC_TEXT_FIELDS) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (patch as any)[key] = specDraft[key];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (patch as any).year = specDraft.year.trim() === '' ? null : specDraft.year;
      // Anything the owner has been through by hand is theirs now, so later
      // reference-data passes leave it alone.
      patch.specs_source = 'manual';
      patch.specs_updated_at = new Date().toISOString();

      setWatch(await watchesApi.update(watch.id, patch));
      setEditingSpecs(false);
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not save those specs.'));
    } finally {
      setSavingSpecs(false);
    }
  }

  /** Fill blanks from the bundled data for a watch added before it had an entry. */
  async function handleLookupSpecs() {
    if (!watch) return;
    const patch = seedPatchFor(watch);
    if (!patch) return;
    setSavingSpecs(true);
    try {
      setWatch(await watchesApi.update(watch.id, patch));
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not fill in those specs.'));
    } finally {
      setSavingSpecs(false);
    }
  }

  async function handleSaveNotes() {
    if (!watch) return;
    setSavingNotes(true);
    setNotesSaved(false);
    try {
      const updated = await watchesApi.update(watch.id, { notes });
      setWatch(updated);
      setError('');
      setNotesSaved(true);
    } catch (err) {
      setError(errText(err, 'Could not save those notes.'));
    } finally {
      setSavingNotes(false);
    }
  }

  async function handleDelete() {
    if (!watch) return;
    if (!window.confirm(`Remove ${watch.brand} ${watch.model} from your list?`)) return;
    try {
      // Delete the photo first, or the object is orphaned in the bucket with
      // nothing left pointing at it. A failure here must not block the delete.
      if (watch.image_url) {
        await watchImages.remove(watch.image_url).catch(() => undefined);
      }
      await watchesApi.remove(watch.id);
      navigate('/watches');
    } catch (err) {
      setError(errText(err, 'Could not remove that watch.'));
    }
  }

  if (missing) {
    return (
      <div className="empty-state">
        <h2>Watch not found</h2>
        <p>It may have been removed. <Link to="/watches">Back to your list</Link>.</p>
      </div>
    );
  }

  if (!watch) {
    return <p className="mt-3">{error ? error : 'Loading…'}</p>;
  }

  // Unsaved edits shouldn't look saved, so the confirmation clears on typing.
  const notesDirty = notes !== (watch.notes ?? '');

  const setSpecs = SPEC_TEXT_FIELDS.filter(k => watch[k]);
  const hasSpecs = setSpecs.length > 0 || watch.year != null;
  // Only offer a lookup when the bundled data actually has something to add.
  const canLookUp = !editingSpecs && seedPatchFor(watch) !== null;

  return (
    <>
      <div className="page-header">
        <Link className="btn btn-outline" to="/watches">
          ← Back
        </Link>
        <h1>
          {watch.brand} {watch.model}
        </h1>
        <button className="btn btn-danger" style={{ marginLeft: 'auto' }} onClick={handleDelete}>
          Remove
        </button>
      </div>

      {error && <p className="auth-error">{error}</p>}

      {/* The file input is always mounted but never shown -- a styled link is
          the control, and a bare <input type="file"> cannot be styled. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handlePickImage}
        style={{ display: 'none' }}
      />

      {/* No photo means no element at all: an empty frame is worse than
          nothing, so the only trace is the "Add photo" action below. */}
      {imageUrl && (
        <div className="watch-photo">
          <img className="watch-photo-img" src={imageUrl} alt={`${watch.brand} ${watch.model}`} />
        </div>
      )}

      <div className="section-title">
        Details
        {!editingCore && (
          <span className="detail-actions">
            <button className="link-btn" onClick={startEditCore}>
              Edit
            </button>
            {/* Only offered once there is a page to re-read. The first read
                happens automatically when the URL is saved. */}
            {watch.source_url && (
              <button
                className="link-btn"
                onClick={() => runScrape(watch)}
                disabled={scraping}
              >
                {scraping ? 'Reading page…' : 'Re-fetch from page'}
              </button>
            )}
            <button
              className="link-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Working…' : watch.image_url ? 'Replace photo' : 'Add photo'}
            </button>
            {watch.image_url && (
              <button className="link-btn" onClick={handleRemoveImage} disabled={uploading}>
                Remove photo
              </button>
            )}
          </span>
        )}
      </div>
      <div className="card">
        {editingCore ? (
          <div className="spec-form">
            <div className="watch-field">
              <label className="auth-label" htmlFor="core-brand">Brand</label>
              <input
                id="core-brand"
                list="detail-brand-options"
                className="auth-input"
                value={coreDraft.brand}
                onChange={e => setCoreDraft({ ...coreDraft, brand: e.target.value })}
              />
              <datalist id="detail-brand-options">
                {BRAND_LIST.map(b => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div className="watch-field">
              <label className="auth-label" htmlFor="core-model">Model</label>
              <input
                id="core-model"
                className="auth-input"
                value={coreDraft.model}
                onChange={e => setCoreDraft({ ...coreDraft, model: e.target.value })}
              />
            </div>
            <div className="watch-field">
              <label className="auth-label" htmlFor="core-reference">
                Reference <span className="optional">optional</span>
              </label>
              <input
                id="core-reference"
                className="auth-input"
                value={coreDraft.reference}
                onChange={e => setCoreDraft({ ...coreDraft, reference: e.target.value })}
              />
            </div>
            <div className="watch-field">
              <label className="auth-label" htmlFor="core-source-url">
                Source page <span className="optional">optional</span>
              </label>
              <input
                id="core-source-url"
                className="auth-input"
                type="url"
                inputMode="url"
                placeholder="https://…"
                value={coreDraft.source_url}
                onChange={e => setCoreDraft({ ...coreDraft, source_url: e.target.value })}
              />
              <p className="field-hint">
                A brand or retailer page for this model. Saving one reads the page
                and fills in any specs and photo you don't already have.
              </p>
            </div>

            <div className="flex gap-1 mt-2 spec-form-actions">
              <button
                className="btn btn-primary"
                onClick={handleSaveCore}
                disabled={savingCore || !coreDraft.brand.trim() || !coreDraft.model.trim()}
              >
                {savingCore ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-outline" onClick={() => setEditingCore(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <table className="specs-table">
            <tbody>
              <tr>
                <td>Brand</td>
                <td>{watch.brand}</td>
              </tr>
              <tr>
                <td>Model</td>
                <td>{watch.model}</td>
              </tr>
              <tr>
                <td>Reference</td>
                <td>{watch.reference || <span className="detail-unset">Not set</span>}</td>
              </tr>
              <tr>
                <td>Source</td>
                <td>
                  {watch.source_url ? (
                    <a
                      className="source-link"
                      href={watch.source_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {hostOf(watch.source_url)}
                    </a>
                  ) : (
                    <span className="detail-unset">Not set</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Added</td>
                <td>{new Date(watch.created_at).toLocaleDateString()}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {scraping && <p className="detail-hint">Reading {hostOf(watch.source_url ?? '')}…</p>}
      {!scraping && scrapeNote && <p className="detail-hint">{scrapeNote}</p>}
      {scrapeWarnings.map(w => (
        <p className="detail-hint detail-warn" key={w}>
          {w}
        </p>
      ))}

      <div className="section-title mt-3">
        Specifications
        {!editingSpecs && (
          <span className="detail-actions">
            {canLookUp && (
              <button className="link-btn" onClick={handleLookupSpecs} disabled={savingSpecs}>
                {savingSpecs ? 'Filling…' : 'Fill from reference data'}
              </button>
            )}
            <button className="link-btn" onClick={startEditSpecs}>
              {hasSpecs ? 'Edit' : 'Add'}
            </button>
          </span>
        )}
      </div>

      <div className="card">
        {editingSpecs ? (
          <div className="spec-form">
            {SPEC_TEXT_FIELDS.map(key => (
              <div className="watch-field" key={key}>
                <label className="auth-label" htmlFor={`spec-${key}`}>
                  {SPEC_LABELS[key]}
                </label>
                <input
                  id={`spec-${key}`}
                  className="auth-input"
                  value={specDraft[key] ?? ''}
                  onChange={e => setSpecDraft({ ...specDraft, [key]: e.target.value })}
                />
              </div>
            ))}
            <div className="watch-field">
              <label className="auth-label" htmlFor="spec-year">
                {SPEC_LABELS.year}
              </label>
              <input
                id="spec-year"
                className="auth-input"
                inputMode="numeric"
                placeholder="e.g. 2023"
                value={specDraft.year ?? ''}
                onChange={e => setSpecDraft({ ...specDraft, year: e.target.value })}
              />
            </div>

            <div className="flex gap-1 mt-2 spec-form-actions">
              <button className="btn btn-primary" onClick={handleSaveSpecs} disabled={savingSpecs}>
                {savingSpecs ? 'Saving…' : 'Save specs'}
              </button>
              <button className="btn btn-outline" onClick={() => setEditingSpecs(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : !hasSpecs ? (
          <p className="detail-empty">
            No specs yet. Known models are filled in automatically when added; for
            anything else, add them by hand.
          </p>
        ) : (
          <table className="specs-table">
            <tbody>
              {setSpecs.map(key => (
                <tr key={key}>
                  <td>{SPEC_LABELS[key]}</td>
                  <td>{watch[key]}</td>
                </tr>
              ))}
              {watch.year != null && (
                <tr>
                  <td>{SPEC_LABELS.year}</td>
                  <td>{watch.year}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Reference data is derived from model knowledge, not a manufacturer
          feed, so it says so rather than presenting itself as confirmed. */}
      {!editingSpecs && hasSpecs && watch.specs_source === 'seed' && (
        <p className="detail-hint">
          From bundled reference data — worth checking against the manufacturer.
          Editing any field marks the specs as yours.
        </p>
      )}
      {!editingSpecs && hasSpecs && watch.specs_source === 'scraped' && watch.source_url && (
        <p className="detail-hint">
          Read off{' '}
          <a className="source-link" href={watch.source_url} target="_blank" rel="noreferrer">
            {hostOf(watch.source_url)}
          </a>{' '}
          — worth checking. Editing any field marks the specs as yours.
        </p>
      )}

      <div className="section-title mt-3">My notes</div>
      <textarea
        className="notes-area"
        value={notes}
        onChange={e => {
          setNotes(e.target.value);
          setNotesSaved(false);
        }}
        placeholder="Sizing, pricing intel, why you want it…"
      />
      <div className="flex gap-1 items-center mt-1">
        <button
          className="btn btn-primary"
          onClick={handleSaveNotes}
          disabled={savingNotes || !notesDirty}
        >
          {savingNotes ? 'Saving…' : 'Save notes'}
        </button>
        {notesSaved && !notesDirty && <span className="detail-hint">Saved</span>}
      </div>

      <div className="section-title mt-3">
        In the news
        {news && news.length > NEWS_SHOWN && (
          <span className="detail-count">
            {NEWS_SHOWN} of {news.length}
          </span>
        )}
      </div>
      <div className="card">
        {news === null ? (
          <p className="detail-empty">Loading…</p>
        ) : news.length === 0 ? (
          <p className="detail-empty">
            No headlines mention this watch yet. Matching is on brand or model, so it
            will pick up {watch.brand} news too.
          </p>
        ) : (
          news.slice(0, NEWS_SHOWN).map(item => (
            <div key={item.id} className="news-item">
              {thumbs !== 'off' && item.image && (
                <a
                  className="news-thumb-link"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  <img
                    className={newsThumbClass(thumbs)}
                    src={item.image}
                    alt=""
                    loading="lazy"
                  />
                </a>
              )}
              <div className="news-text">
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.title}
                </a>
                <div className="news-meta">
                  {item.source}
                  {item.published_at
                    ? ` · ${new Date(item.published_at).toLocaleDateString()}`
                    : ''}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
